import React, { useEffect, useState, useMemo } from "react";
import brokerService from "../../services/brokerService";
import { usePermission } from "../../hooks/usePermissions";
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import { formatAdminDate } from "../../utils/datetime";
import "../../pages/PlansManagement/PlansManagement.css";

const getFullImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = REACT_APP_API_BASE_URL.replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
};

/* ── SVG Icons (same as PlansManagement) ─────────────────────── */
const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* ── Empty form ──────────────────────────────────────────────── */
const EMPTY_FORM = {
  name: "",
  broker_code: "",
  is_active: true,
  sort_order: 0,
  logo_url: "",
};

/* ── Skeleton rows (same pattern as Plans) ───────────────────── */
const SkeletonRows = () => (
  <>
    {[1, 2, 3, 4, 5].map((index) => (
      <tr key={index} className="pm-skeleton-row">
        {[40, 120, 80, 70, 60, 50, 100, 90].map((width, cellIndex) => (
          <td key={cellIndex}>
            <span className="pm-skel" style={{ width }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* ── Delete Modal ────────────────────────────────────────────── */
const DeleteModal = ({ broker, onClose, onConfirm, saving, error }) => (
  <div className="pm-modal-overlay">
    <div className="pm-modal pm-modal-max-400">
      <div className="pm-modal-header">
        <h2>Delete Broker</h2>
        <button className="pm-modal-close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <div className="pm-modal-body pm-modal-body-centered">
        <div className="pm-confirm-icon">🗑️</div>
        <p className="pm-confirm-msg">Are you sure you want to delete</p>
        <p className="pm-confirm-name">"{broker.name}"?</p>
        <p className="pm-error-text">This action cannot be undone.</p>
        {error && <div className="pm-alert" style={{ marginTop: 12 }}>{error}</div>}
      </div>
      <div className="pm-modal-footer pm-justify-center">
        <button className="pm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="pm-btn-delete" onClick={() => onConfirm(broker.id)} disabled={saving}>
          {saving ? "Deleting..." : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
);

/* ── Add/Edit Modal ──────────────────────────────────────────── */
const BrokerFormModal = ({ mode, broker, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState(
    mode === "edit" && broker
      ? {
          name: broker.name || "",
          broker_code: broker.broker_code || "",
          is_active: !!broker.is_active,
          sort_order: broker.sort_order ?? 0,
          logo_url: broker.logo_url || "",
        }
      : { ...EMPTY_FORM }
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.name || "").trim()) nextErrors.name = "Broker name is required";
    if (mode === "add" && !String(form.broker_code || "").trim()) nextErrors.broker_code = "Broker code is required";
    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSave(form, selectedFile);
  };

  return (
    <div className="pm-modal-overlay">
      <div className="pm-modal">
        <div className="pm-modal-header">
          <h2>{mode === "add" ? "+ Add New Broker" : "✏️ Edit Broker"}</h2>
          <button className="pm-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="pm-modal-body">
          {error && <div className="pm-alert">{error}</div>}

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Broker Name <span className="required">*</span></label>
              <input
                className={`pm-input${errors.name ? " error" : ""}`}
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g. AngelOne"
              />
              {errors.name && <span className="pm-input-error">{errors.name}</span>}
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>
                Broker Code <span className="required">*</span>
                {mode === "edit" && <span style={{ fontWeight: 400, color: "var(--text-light)", fontSize: "var(--fs-xs)", marginLeft: 6 }}>(read-only)</span>}
              </label>
              <input
                className={`pm-input${errors.broker_code ? " error" : ""}`}
                value={form.broker_code}
                onChange={(e) => handleChange("broker_code", e.target.value)}
                disabled={mode === "edit"}
                placeholder="e.g. angelone"
              />
              {errors.broker_code && <span className="pm-input-error">{errors.broker_code}</span>}
            </div>
            <div className="pm-form-group">
              <label>Sort Order</label>
              <input
                className="pm-input"
                type="number"
                value={form.sort_order}
                onChange={(e) => handleChange("sort_order", Number(e.target.value))}
                min="0"
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group">
              <label>Active Status</label>
              <select className="pm-select" value={form.is_active ? "Yes" : "No"} onChange={(e) => handleChange("is_active", e.target.value === "Yes")}>
                <option value="Yes">Active</option>
                <option value="No">Inactive</option>
              </select>
            </div>
            <div className="pm-form-group">
              <label>Logo URL (Text)</label>
              <input
                className="pm-input"
                type="text"
                value={form.logo_url}
                onChange={(e) => handleChange("logo_url", e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="pm-form-row">
            <div className="pm-form-group full">
              <label>Logo File (PNG, JPEG, WebP)</label>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                style={{ padding: "9px 0", fontSize: "var(--body-base)" }}
              />
              {previewUrl ? (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={previewUrl} alt="Preview" style={{ height: 40, objectFit: "contain", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)" }} />
                  <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>{selectedFile?.name}</span>
                </div>
              ) : form.logo_url ? (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={getFullImageUrl(form.logo_url)} alt="Current" style={{ height: 40, objectFit: "contain", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)" }} />
                  <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>Current logo</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pm-btn-save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : mode === "add" ? "+ Create Broker" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────── */
const BrokerManagement = () => {
  // Broker Management is part of the Accesscontrol surface — the same access
  // type broker.admin.routes gates every write on, and the one the route and
  // sidebar entry already use for VIEW. Reaching this page only proves VIEW,
  // so each write control is gated on its own right below. The server checks
  // these independently; this only stops offering actions that would 403.
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("CREATE_ACCESSCONTROL");
  const canModify = hasPermission("MODIFY_ACCESSCONTROL");
  const canDelete = hasPermission("DELETE_ACCESSCONTROL");

  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal
  const [modalMode, setModalMode] = useState(null); // null | 'add' | 'edit' | 'delete'
  const [selectedBroker, setSelectedBroker] = useState(null);

  // Search
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // ── Data fetch ────────────────────────────────────────────────
  const loadBrokers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await brokerService.getAll();
      setBrokers(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load brokers");
      setBrokers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrokers();
  }, []);

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // ── Filtered list ─────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = appliedSearch.toLowerCase().trim();
    if (!q) return brokers;
    return brokers.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.broker_code || "").toLowerCase().includes(q)
    );
  }, [brokers, appliedSearch]);

  // ── Modal helpers ─────────────────────────────────────────────
  const openAdd = () => {
    setSelectedBroker(null);
    setModalMode("add");
    setError("");
  };

  const openEdit = (broker) => {
    setSelectedBroker(broker);
    setModalMode("edit");
    setError("");
  };

  const openDelete = (broker) => {
    setSelectedBroker(broker);
    setModalMode("delete");
    setError("");
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedBroker(null);
    setError("");
  };

  // ── CRUD handlers ─────────────────────────────────────────────
  const handleSave = async (formData, selectedFile) => {
    setSaving(true);
    setError("");
    try {
      let brokerId;
      if (modalMode === "add") {
        const res = await brokerService.create(formData);
        brokerId = res?.data?.id;
        setSuccess("Broker created successfully");
      } else {
        const { broker_code, ...updateData } = formData;
        await brokerService.update(selectedBroker.id, updateData);
        brokerId = selectedBroker.id;
        setSuccess("Broker updated successfully");
      }

      // Upload logo if a file was selected
      if (selectedFile && brokerId !== undefined) {
        await brokerService.uploadLogo(brokerId, selectedFile);
        setSuccess(modalMode === "add" ? "Broker and logo created successfully" : "Broker and logo updated successfully");
      }

      await loadBrokers();
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to save broker");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    setError("");
    try {
      await brokerService.delete(id);
      setSuccess("Broker deleted successfully");
      await loadBrokers();
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to delete broker");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (broker) => {
    try {
      await brokerService.toggleActive(broker.id);
      setSuccess(`${broker.name} ${broker.is_active ? "deactivated" : "activated"}`);
      await loadBrokers();
    } catch (err) {
      setError(err?.message || "Failed to toggle broker status");
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="pm-container">
      <div className="pm-page-header">
        <div className="pm-header-left">
          <h1 className="pm-page-title">Broker Management</h1>
          <p className="pm-page-subtitle">{filtered.length} broker{filtered.length !== 1 ? "s" : ""} found</p>
        </div>
        <div className="pm-header-right">
          {canCreate && (
            <button className="pm-btn-add" onClick={openAdd}>
              <PlusIcon /> Add New Broker
            </button>
          )}
        </div>
      </div>

      {error && !modalMode && <div className="pm-alert">{error}</div>}
      {success && (
        <div style={{
          marginBottom: 16, padding: "12px 16px",
          borderRadius: "var(--radius-lg)",
          background: "var(--color-success-bg)", color: "var(--color-success)",
          fontSize: "var(--fs-base)", fontWeight: 600
        }}>
          {success}
        </div>
      )}

      <div className="pm-table-filters">
        <div className="pm-filters-left">
          <span className="pm-filter-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="pm-filters-right">
          <div className="pm-search-wrap">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(search)}
            />
          </div>
          <button className="pm-btn-search" onClick={() => setAppliedSearch(search)}>Search</button>
        </div>
      </div>

      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Broker Name</th>
              <th>Code</th>
              <th className="center">Status</th>
              <th className="center">Sort</th>
              <th className="center">Logo</th>
              <th>Updated</th>
              <th className="center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : filtered.length === 0 ? (
              <tr className="pm-empty-row">
                <td colSpan={8}>
                  <div className="pm-empty-icon">🏦</div>
                  <h3>No brokers found</h3>
                  <p>Try adjusting your search query.</p>
                </td>
              </tr>
            ) : (
              filtered.map((broker) => (
                <tr key={broker.id}>
                  <td><span className="pm-cell-id">#{broker.id}</span></td>
                  <td>
                    <div className="pm-cell-name">{broker.name}</div>
                  </td>
                  <td>
                    <span className="pm-access-pill">{broker.broker_code}</span>
                  </td>
                  <td className="center">
                    <span
                      className={`pm-status-badge ${broker.is_active ? "active" : "inactive"}`}
                      onClick={canModify ? () => handleToggleActive(broker) : undefined}
                      style={{ cursor: canModify ? "pointer" : "default" }}
                      title={canModify ? "Click to toggle" : undefined}
                    >
                      {broker.is_active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </td>
                  <td className="center">{broker.sort_order}</td>
                  <td className="center">
                    {broker.logo_url ? (
                      <img
                        src={getFullImageUrl(broker.logo_url)}
                        alt={broker.name}
                        style={{ width: 32, height: 32, borderRadius: "var(--radius-md)", objectFit: "contain", border: "1px solid var(--border-light)" }}
                      />
                    ) : (
                      <span className="pm-text-muted-dash">—</span>
                    )}
                  </td>
                  <td>{formatAdminDate(broker.updated_at, "—")}</td>
                  <td className="center">
                    <div className="pm-actions-cell">
                      {canModify && (
                        <button className="pm-icon-btn edit" title="Edit" onClick={() => openEdit(broker)}>
                          <PencilIcon />
                        </button>
                      )}
                      {canDelete && broker.id !== 0 && (
                        <button className="pm-icon-btn delete" title="Delete" onClick={() => openDelete(broker)}>
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filtered.length > 0 && (
          <div className="pm-table-footer">
            <span>Showing {filtered.length} of {brokers.length} brokers</span>
            <span className="pm-pagination-count">
              Last refreshed: {formatAdminDate(new Date())}
            </span>
          </div>
        )}
      </div>

      {(modalMode === "add" || modalMode === "edit") && (
        <BrokerFormModal
          mode={modalMode}
          broker={selectedBroker}
          onClose={closeModal}
          onSave={handleSave}
          saving={saving}
          error={modalMode && error ? error : ""}
        />
      )}
      {modalMode === "delete" && selectedBroker && (
        <DeleteModal
          broker={selectedBroker}
          onClose={closeModal}
          onConfirm={handleDelete}
          saving={saving}
          error={modalMode === "delete" && error ? error : ""}
        />
      )}
    </div>
  );
};

export default BrokerManagement;
